// @ts-strict-ignore
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { slashJoin } from 'path-slashes';
import { BehaviorSubject, combineLatest, distinctUntilChanged, EMPTY, filter, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';

import { AppErrorMajor } from 'src/app/core/app-error/app-error-major.enum';
import { AppError } from 'src/app/core/app-error/app-error.interface';
import { AppErrorService } from 'src/app/core/app-error/app-error.service';
import { DevModeService } from 'src/app/core/dev/dev-mode/dev-mode.service';
import { ActivatedRouteState } from 'src/app/core/services/activated-route/activated-route-state.interface';
import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import { seamlessStoreFns } from 'src/app/store/common/common.utils';
import { selectRouteState } from 'src/app/store/router/router.selectors';
import { selectConfigAndTranslationRelevantParams } from 'src/app/store/runtime-variables/runtime-variables.selectors';
import { secureRandom } from 'src/app/utils/functions/secure-random';
import { Data } from 'src/app/utils/models/data.interface';
import { Subprocess } from 'src/app/utils/models/subprocess.enum';

import { CarlDataType } from './carl-data-type.enum';

export interface LatestRequestStatus {
  url: string;
  type: CarlDataType;
  status: boolean;
  error: HttpErrorResponse;
}

@Injectable({
  providedIn: 'root'
})
export class CarlService {
  private readonly latestRequestStatusSubject = new BehaviorSubject<LatestRequestStatus>({
    url: null,
    type: null,
    status: false,
    error: null
  });

  public constructor(
    private readonly store: Store,
    private readonly httpClient: HttpClient,
    private readonly appErrorService: AppErrorService,
    private readonly devModeService: DevModeService
  ) {}

  public get latestRequestStatus$(): Observable<LatestRequestStatus> {
    return this.latestRequestStatusSubject.asObservable();
  }

  public loadConfig(): Observable<Data> {
    return this.requestData(CarlDataType.Configuration);
  }

  public loadTranslations(): Observable<Data> {
    return this.requestData(CarlDataType.Translations);
  }

  public getUrl(type: CarlDataType): Observable<string> {
    const getQueryParametersAsString = (
      carlLabel: string | undefined,
      userChooser: string | undefined,
      subprocess: Subprocess = Subprocess.CCci,
      businessType = 'B2C',
      isDevMode = false
    ): string => {
      const queryParams = [
        `contexts=${[subprocess, businessType, userChooser].filter(param => !!param).join(',')}`,
        carlLabel ? `label=${carlLabel.trim()}` : null,
        isDevMode ? `cb=devMode-${secureRandom().toString().slice(2)}` : null
      ];
      return queryParams.filter(param => !!param).join('&');
    };

    return combineLatest([
      this.store.pipe(selectConfigAndTranslationRelevantParams, distinctUntilChanged()),
      seamlessStoreFns.observeBusinessTypeInSeamlessStore(),
      this.devModeService.isDevMode$
    ]).pipe(
      distinctUntilChanged(),
      filter(([{ country, language, carlUrl }]) => !!(country && language && carlUrl)),
      map(([{ subprocess, country, language, carlUrl, carlLabel, userChooser }, businessContext, isDevMode]) => {
        const queryParameters = getQueryParametersAsString(
          carlLabel,
          userChooser,
          subprocess,
          businessContext,
          isDevMode
        );
        return [slashJoin(carlUrl, type, country, language), queryParameters].filter(urlParts => !!urlParts).join('?');
      })
    );
  }

  private requestData(type: CarlDataType): Observable<Data> {
    return this.getUrl(type).pipe(
      withLatestFrom(this.store.select(selectRouteState)),
      mergeMap(
        ([url, routeState]: [string, ActivatedRouteState]): Observable<Data> =>
          this.httpClient.get<Data>(url).pipe(
            tap((): void => {
              this.latestRequestStatusSubject.next({ url, type, status: true, error: null });
            }),
            catchError(this.handleRequestError(url, routeState, type))
          )
      )
    );
  }

  private handleRequestError(
    url: string,
    routeState: ActivatedRouteState,
    requestType: CarlDataType
  ): (error: HttpErrorResponse) => Observable<unknown> | Observable<never> {
    return (error: HttpErrorResponse) => {
      this.latestRequestStatusSubject.next({
        url,
        type: requestType,
        status: false,
        error
      });

      if (!!routeState && routeState.appRoutePath === AppRoutePath.Doorway) {
        return of({});
      }

      const currentError: AppError = {
        statusCode: error.status,
        message: error.message,
        name: error.name,
        type: AppErrorMajor.CarlNotAvailable
      };
      this.appErrorService.handleMajorError(currentError);

      return EMPTY;
    };
  }
}
